import { Box, Button, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import ContractTypeSelect from "../../../../shared/components/ContractTypeSelect";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import OfficesSelect from "../../../../shared/components/OfficeSelect";
import Select from "../../../../shared/components/Select";
import useApi from "../../../../shared/hooks/useApi";
import { ContractTypeId, OfficeId } from "../../../../shared/schema/schema";
import {
  getNextWeekRange,
  RnSchedulingStatus,
  useRnSchedulingMapFilters,
} from "../hooks/useRnSchedulingMapFilters";
import {
  getRegionLabel,
  isNycBoroughCounty,
  SCHEDULING_STATUS_LABEL,
  SCHEDULING_STATUSES,
} from "../rn-scheduling-map.types";

const SCHEDULING_STATUS_OPTIONS: { value: RnSchedulingStatus; label: string }[] =
  SCHEDULING_STATUSES.map((status) => ({ value: status, label: SCHEDULING_STATUS_LABEL[status] }));

/** NYC boroughs first, as most patients live there, then every other county by name. */
function toRegionOptions(countyNames: string[]): { value: string; label: string }[] {
  return countyNames
    .map((countyName) => ({ value: countyName, label: getRegionLabel(countyName) }))
    .sort((a, b) => {
      const aIsBorough = isNycBoroughCounty(a.value);
      const bIsBorough = isNycBoroughCounty(b.value);

      if (aIsBorough !== bIsBorough) {
        return aIsBorough ? -1 : 1;
      }

      return a.label.localeCompare(b.label);
    });
}

export default function RnSchedulingMapFilters() {
  const { queries } = useApi();
  const filters = useRnSchedulingMapFilters();
  const countiesQuery = useQuery(queries.rnSchedulingMap.counties());

  // While the user is picking a range, react-datepicker reports the start date
  // with a null end date. The endpoint needs both, so the half-picked range is
  // held here and only committed to the filters once the end date is chosen.
  const [pendingRange, setPendingRange] = React.useState<[LocalDate, LocalDate | null] | null>(
    null
  );

  const committedFrom = filters.getValue("certificationPeriodEndFrom") ?? null;
  const committedTo = filters.getValue("certificationPeriodEndTo") ?? null;

  const startDate = pendingRange !== null ? pendingRange[0] : committedFrom;
  const endDate = pendingRange !== null ? pendingRange[1] : committedTo;

  const contractTypeIds = filters.getValue("contractTypeIds") ?? [];
  const countyNames = filters.getValue("countyNames") ?? [];
  const officeIds = filters.getValue("officeIds") ?? [];
  const schedulingStatuses = filters.getValue("schedulingStatuses") ?? [];

  const regionOptions = React.useMemo(
    () => toRegionOptions(countiesQuery.data ?? []),
    [countiesQuery.data]
  );

  function handleDateRangeChange(value: [LocalDate, LocalDate | null] | null) {
    if (value === null) {
      const nextWeek = getNextWeekRange();
      setPendingRange(null);
      filters.setValues({
        certificationPeriodEndFrom: nextWeek.from,
        certificationPeriodEndTo: nextWeek.to,
      });
      return;
    }

    const [from, to] = value;

    if (to === null) {
      setPendingRange([from, null]);
      return;
    }

    setPendingRange(null);
    filters.setValues({ certificationPeriodEndFrom: from, certificationPeriodEndTo: to });
  }

  function handleContractChange(value: ContractTypeId[] | undefined) {
    filters.setValues({ contractTypeIds: value ?? [] });
  }

  function handleCountyChange(value: string[] | undefined) {
    filters.setValues({ countyNames: value ?? [] });
  }

  function handleOfficeChange(value: OfficeId[] | undefined) {
    filters.setValues({ officeIds: value ?? [] });
  }

  function handleSchedulingStatusChange(value: RnSchedulingStatus[] | undefined) {
    filters.setValues({ schedulingStatuses: value ?? [] });
  }

  function handleClearFilters() {
    setPendingRange(null);
    filters.resetToDefault();
  }

  return (
    <Box bg="white" borderRadius="md" p={3}>
      <Wrap align="center" spacing={2}>
        <WrapItem>
          <Text color="gray.600" fontSize="sm" fontWeight="medium">
            Certification period ends
          </Text>
        </WrapItem>
        <WrapItem>
          <RangeDatePicker
            endDate={endDate}
            inputProps={{ placeholder: "Certification period end range", size: "sm" }}
            startDate={startDate}
            onChange={handleDateRangeChange}
          />
        </WrapItem>
        <WrapItem>
          <ContractTypeSelect
            buttonProps={{ size: "sm" }}
            multiple={true}
            size="sm"
            value={contractTypeIds.length > 0 ? contractTypeIds : null}
            onChange={handleContractChange}
          />
        </WrapItem>
        <WrapItem>
          <OfficesSelect
            buttonProps={{ size: "sm" }}
            multiple={true}
            size="sm"
            value={officeIds.length > 0 ? officeIds : null}
            onChange={handleOfficeChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            isLoading={countiesQuery.isPending}
            label="Borough / Region"
            multiple={true}
            options={regionOptions}
            searchable={true}
            size="sm"
            value={countyNames.length > 0 ? countyNames : null}
            onChange={handleCountyChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            label="Status"
            multiple={true}
            options={SCHEDULING_STATUS_OPTIONS}
            size="sm"
            value={schedulingStatuses.length > 0 ? schedulingStatuses : null}
            onChange={handleSchedulingStatusChange}
          />
        </WrapItem>
        <WrapItem>
          <Button size="sm" variant="ghost" onClick={handleClearFilters}>
            Clear filters
          </Button>
        </WrapItem>
      </Wrap>
    </Box>
  );
}
